import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../../../common/shared.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
    CreateInsuranceTypeCoverageDto,
    CreateCoverageCurrencyDto,
    CurrencyEntryDto,
    InsuranceTypeCoverageDto
} from '../../../../core/data/new-dtos/insurance-type.dtos';
import { CoverageRateSelectorComponent } from '../coverage-rate-selector/coverage-rate-selector.component';

@Component({
    selector: 'app-coverage-item',
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule, NgxMaskDirective, NgSelectModule, CoverageRateSelectorComponent],
    templateUrl: './coverage-item.component.html',
    styleUrls: ['./coverage-item.component.scss'],
    providers: [provideNgxMask()],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageItemComponent implements OnInit {
    @Input() coverage!: CreateInsuranceTypeCoverageDto;
    @Input() coverageName: string = '';
    @Input() coverageCode: string = '';
    @Input() availableCurrencies: CurrencyEntryDto[] = [];
    @Input() disabled: boolean = false;

    @Output() coverageChange = new EventEmitter<CreateInsuranceTypeCoverageDto | InsuranceTypeCoverageDto>();
    @Output() remove = new EventEmitter<void>();

    // Input to determine if tariff selector should be shown
    @Input() formulaId!: string;

    // Local UI state - determines if fraction input is shown
    useCustomFraction: boolean = false;

    tariffOptions = [
        { label: 'Vida (Tabla Mortalidad)', value: 'VidaCalculos' },
        { label: 'Incapacidad (PACI)', value: 'Paci' },
        { label: 'Enfermedades Graves (EG)', value: 'Eg' },
        { label: 'Accidental', value: 'Accidental' }
    ];

    get showTariffSelector(): boolean {
        return this.formulaId === 'VidaTemporalNivelado';
    }

    get isRequired(): boolean {
        return this.coverage.isRequired;
    }

    get showRateSelector(): boolean {
        // Show for Personal Accident (Individual or potentially others if needed)
        // Adjust formula ID check as needed based on your exact ID string
        return this.formulaId === 'AccidentesPersonalesIndividual';
    }

    ngOnInit(): void {
        // Ensure categories array exists
        if (!this.coverage.categories) {
            this.coverage.categories = [];
        }

        // Initialize useCustomFraction based on existing fraction value
        this.useCustomFraction = this.coverage.insurePrimaryFraction > 0;

        // If loaded as required (and not a % of the sum assured), ensure values match global limits
        if (this.coverage.isRequired && !this.useCustomFraction && this.availableCurrencies?.length > 0) {
            this.enforcePrimaryLimits();
        }
    }

    onPrimaryChange(value: boolean): void {
        this.coverage.isRequired = value;
        if (value && !this.useCustomFraction) {
            // Enforce global limits for all selected currencies (a % coverage keeps its fraction instead)
            this.enforcePrimaryLimits();
        }
        this.emitChange();
    }

    onUseCustomFractionChange(value: boolean): void {
        this.useCustomFraction = value;
        if (value) {
            // Reset currency limits when custom fraction is enabled
            this.coverage.currencies.forEach(c => {
                c.minAmount = 0;
                c.maxAmount = 0;
            });
        }
        if (!value) {
            // Reset fraction when unchecked
            this.coverage.insurePrimaryFraction = 0;
        }
        this.emitChange();
    }

    onFractionChange(value: number): void {
        this.coverage.insurePrimaryFraction = value;
        this.emitChange();
    }

    getCurrencyByGlobalId(currencyId: number): any {
        return this.coverage.currencies.find(c => c.currencyId === currencyId);
    }

    updateCurrencyMaxAmount(currencyId: number, maxAmount: number): void {
        const currency = this.coverage.currencies.find(c => c.currencyId === currencyId);
        if (currency) {
            currency.maxAmount = maxAmount;
            this.emitChange();
        }
    }

    updateCurrencyMinAmount(currencyId: number, minAmount: number): void {
        const currency = this.coverage.currencies.find(c => c.currencyId === currencyId);
        if (currency) {
            currency.minAmount = minAmount;
            if (currency.isFixedLimit) {
                currency.maxAmount = minAmount;
            }
            this.emitChange();
        }
    }

    onFixedLimitChange(currencyId: number, value: boolean): void {
        const currency = this.coverage.currencies.find(c => c.currencyId === currencyId);
        if (currency) {
            currency.isFixedLimit = value;
            if (value) {
                currency.maxAmount = currency.minAmount;
            }
            this.emitChange();
        }
    }
    private enforcePrimaryLimits(): void {
        this.coverage.currencies.forEach(c => {
            const global = this.availableCurrencies.find(g => g.id === c.currencyId);
            if (global && !c.isFixedLimit) {
                c.minAmount = global.min;
                c.maxAmount = global.max;
            }
        });
    }

    updateCurrencyMinPremium(currencyId: number, value: number | string | null): void {
        const currency = this.coverage.currencies.find(c => c.currencyId === currencyId);
        if (currency) {
            currency.minPremium = value === '' || value == null ? null : Number(value);
            this.emitChange();
        }
    }

    getCurrencyIndex(currencyId: number): number {
        return this.coverage.currencies.findIndex(c => c.currencyId === currencyId);
    }

    getCurrencyLabel(currency: CurrencyEntryDto): string {
        return `${currency.currency} (${currency.min.toLocaleString()} - ${currency.max.toLocaleString()})`;
    }

    onRemove(): void {
        this.remove.emit();
    }

    emitChange(): void {
        this.coverageChange.emit({ ...this.coverage, currencies: [...this.coverage.currencies] });
    }
}
